import { useField } from "formik";
import type { ChangeEvent, ComponentProps } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

/** Inline field error, shown once the field is touched (incl. after a blocked
 * Save/Preview, which deep-marks errored fields touched). */
function FieldError({ touched, error }: { touched: boolean; error?: string }) {
  if (!touched || !error) return null;
  return <p className="text-destructive mt-1 text-xs">{error}</p>;
}

/** Formik-bound shadcn Checkbox with an optional inline label. */
export function FormikCheckbox({
  name,
  label,
  "aria-label": ariaLabel,
}: {
  name: string;
  label?: string;
  "aria-label"?: string;
}) {
  const [field, , helpers] = useField({ name, type: "checkbox" });
  const checkbox = (
    <Checkbox
      id={label ? name : undefined}
      aria-label={ariaLabel}
      checked={!!field.value}
      onCheckedChange={(v) => helpers.setValue(v === true)}
    />
  );
  if (!label) return checkbox;
  return (
    <Label htmlFor={name} className="flex items-center gap-2 font-normal">
      {checkbox}
      {label}
    </Label>
  );
}

/** Formik-bound shadcn Select. Empty value ("") shows the placeholder. */
export function FormikSelect({
  name,
  options,
  placeholder,
  className,
  "aria-label": ariaLabel,
}: {
  name: string;
  options: { value: string; label: string }[];
  placeholder?: string;
  className?: string;
  "aria-label"?: string;
}) {
  const [field, meta, helpers] = useField(name);
  return (
    <>
      <Select value={field.value || undefined} onValueChange={(v) => helpers.setValue(v)}>
        <SelectTrigger className={className} aria-label={ariaLabel} aria-invalid={!!(meta.touched && meta.error)}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <FieldError touched={meta.touched} error={meta.error} />
    </>
  );
}

/** Formik-bound shadcn Input without the FormField label chrome — for grid cells. */
export function FormikCell({
  name,
  className,
  ...props
}: { name: string } & Omit<ComponentProps<typeof Input>, "name">) {
  const [field, meta] = useField(name);
  return (
    <>
      <Input
        id={name}
        className={cn("h-9", className)}
        aria-invalid={!!(meta.touched && meta.error)}
        {...field}
        {...props}
      />
      <FieldError touched={meta.touched} error={meta.error} />
    </>
  );
}

/** Formik-bound shadcn Textarea (for free-text narratives). */
export function FormikTextarea({
  name,
  className,
  ...props
}: { name: string } & Omit<ComponentProps<typeof Textarea>, "name">) {
  const [field, meta] = useField(name);
  return (
    <>
      <Textarea
        id={name}
        className={className}
        aria-invalid={!!(meta.touched && meta.error)}
        {...field}
        {...props}
      />
      <FieldError touched={meta.touched} error={meta.error} />
    </>
  );
}

/**
 * Formik-bound Yes/No radio pair (stores "Y" / "N"). Used for the ISA wizard's
 * eligibility questions; the backend maps "Y"/"N" onto the paired PDF checkboxes.
 */
export function FormikRadioYN({
  name,
  "aria-label": ariaLabel,
}: {
  name: string;
  "aria-label"?: string;
}) {
  const [field, , helpers] = useField<string>(name);
  return (
    <RadioGroup
      className="flex gap-6"
      aria-label={ariaLabel}
      value={field.value || ""}
      onValueChange={(v) => helpers.setValue(v)}
    >
      <Label htmlFor={`${name}-yes`} className="flex items-center gap-2 font-normal">
        <RadioGroupItem id={`${name}-yes`} value="Y" /> Yes
      </Label>
      <Label htmlFor={`${name}-no`} className="flex items-center gap-2 font-normal">
        <RadioGroupItem id={`${name}-no`} value="N" /> No
      </Label>
    </RadioGroup>
  );
}

/**
 * Formik-bound radio group with arbitrary options (stores the option value).
 * Used where a question has more than the Yes/No pair (e.g. marital status,
 * education level); the backend maps the stored value onto the matching PDF
 * checkbox.
 */
export function FormikRadioGroup({
  name,
  options,
  className,
  "aria-label": ariaLabel,
}: {
  name: string;
  options: { value: string; label: string }[];
  className?: string;
  "aria-label"?: string;
}) {
  const [field, , helpers] = useField<string>(name);
  return (
    <RadioGroup
      className={cn("flex flex-col gap-2", className)}
      aria-label={ariaLabel}
      value={field.value || ""}
      onValueChange={(v) => helpers.setValue(v)}
    >
      {options.map((o) => (
        <Label
          key={o.value}
          htmlFor={`${name}-${o.value}`}
          className="flex items-center gap-2 font-normal"
        >
          <RadioGroupItem id={`${name}-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

function formatSsn(raw: string): string {
  const d = raw.replace(/\D/g, "").slice(0, 9);
  if (d.length > 5) return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
  if (d.length > 3) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return d;
}

/**
 * Formik-bound SSN input. Formats live to ###-##-#### and stores that string
 * (the server re-validates as 9 digits). Sensitive data — `autoComplete` is off
 * and the value is never logged (see the sensitive-data-handling rules).
 */
export function FormikSsnField({
  name,
  className,
  "aria-label": ariaLabel,
}: {
  name: string;
  className?: string;
  "aria-label"?: string;
}) {
  const [field, meta, helpers] = useField<string>(name);
  return (
    <>
      <Input
        id={name}
        className={cn("h-9", className)}
        inputMode="numeric"
        autoComplete="off"
        aria-label={ariaLabel}
        placeholder="###-##-####"
        aria-invalid={!!(meta.touched && meta.error)}
        value={field.value ?? ""}
        onBlur={field.onBlur}
        onChange={(e: ChangeEvent<HTMLInputElement>) => helpers.setValue(formatSsn(e.target.value))}
      />
      <FieldError touched={meta.touched} error={meta.error} />
    </>
  );
}

function addThousands(raw: string): string {
  if (!raw) return "";
  const [intPart = "", ...rest] = raw.split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return rest.length ? `${grouped}.${rest.join("")}` : grouped;
}

/**
 * Formik-bound currency/count cell with a live thousands-separator display for
 * the ISA Part III allocation grid. Stores the digits (with an optional decimal)
 * — the server strips separators and validates numeric. Pass `integer` for the
 * exemption-count row (whole numbers, no decimals).
 */
export function FormikMoneyCell({
  name,
  className,
  integer = false,
  "aria-label": ariaLabel,
}: {
  name: string;
  className?: string;
  integer?: boolean;
  "aria-label"?: string;
}) {
  const [field, meta, helpers] = useField<string>(name);
  const raw = field.value ?? "";
  const display = integer ? raw : addThousands(raw);
  return (
    <>
      <Input
        id={name}
        className={cn("h-9 text-right", className)}
        inputMode={integer ? "numeric" : "decimal"}
        aria-label={ariaLabel}
        aria-invalid={!!(meta.touched && meta.error)}
        value={display}
      onBlur={field.onBlur}
      onChange={(e: ChangeEvent<HTMLInputElement>) => {
        const allowed = integer ? /[^\d]/g : /[^\d.]/g;
        let cleaned = e.target.value.replace(/,/g, "").replace(allowed, "");
        if (!integer) {
          // Keep only the first decimal point.
          const first = cleaned.indexOf(".");
          if (first !== -1) {
            cleaned = cleaned.slice(0, first + 1) + cleaned.slice(first + 1).replace(/\./g, "");
          }
        }
        helpers.setValue(cleaned);
      }}
      />
      <FieldError touched={meta.touched} error={meta.error} />
    </>
  );
}
